"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useTranslations } from "next-intl";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppForm, type AppFormReturn } from "@/hooks/useAppForm";
import { useModal } from "@/components/providers/modal-provider/ModalProvider";
import NotificationMessage from "@/components/atoms/notifications/NotificationMessage";
import type { StepperFormStep } from "@/components/organisms/StepperForm/schema";
import { useUpdateListingResourceId } from "@/components/modules/listings/api/mutations/useUpdateListingResourceId";
import { useAdminUpdateListingResourceId } from "@/components/modules/admin/api/mutations/useAdminUpdateListingResourceId";
import { useBackofficeListingContext } from "@/components/modules/admin/hooks/useBackofficeListingContext";
import type { PortfolioRoomsResult } from "@/components/modules/portfolio/hooks/usePortfolioUtils";
import type { BusinessUnitsSchemaType, GetRealEstateManagementSchemaType } from "@/components/modules/portfolio/api/schemas/schemas";
import { useUpdateRoomListingMutation } from "@/components/modules/listings/api/mutations/useUpdateRoomListingMutation";
import { useSelectedPropertyDetail } from "./useSelectedPropertyDetail";
import { AssociateUnitStep } from "./associate-unit-step/AssociateUnitStep";
import { AssociatePhotosStep } from "./associate-photos-step/AssociatePhotosStep";
import { AssociateFieldsStep } from "./associate-fields-step/AssociateFieldsStep";
import { buildSharedFieldsUpdate, type PropertyShared } from "./sharedFieldsToApply";
type AssociateUnitSelection = {
  unit: BusinessUnitsSchemaType;
  portfolio: GetRealEstateManagementSchemaType;
};
type AssociateFormValues = {
  selectedUnitId: string;
  selectedUnitTitle: string;
  fieldsToApply: string[];
};
type Args = {
  resource: PortfolioRoomsResult;
  listingId?: string;
  currentUnitId?: string | null;
  onSuccess?: () => void;
};
const findUnitAndPortfolio = (resource: PortfolioRoomsResult, unitId: string): AssociateUnitSelection | undefined => {
  for (const portfolio of resource.portfolios) {
    for (const business of portfolio.businesses ?? []) {
      const unit = (business.units ?? []).find((u: BusinessUnitsSchemaType) => u.id === unitId);
      if (unit) return {
        unit,
        portfolio
      };
    }
  }
  return undefined;
};
export function useAssociateListingStepper(t0) {
  const $ = _c(88);
  const {
    resource,
    listingId,
    currentUnitId,
    onSuccess
  } = t0;
  const t = useTranslations("modules.portfolio.associateListingToUnit");
  const tStepper = useTranslations("modules.portfolio.associateListingToUnit.stepper");
  const {
    closeModal
  } = useModal();
  const {
    isBackofficeView
  } = useBackofficeListingContext();
  const associateMutation = useUpdateListingResourceId();
  const adminAssociateMutation = useAdminUpdateListingResourceId();
  const updateListingMutation = useUpdateRoomListingMutation();
  let t1;
  if ($[0] !== tStepper) {
    t1 = z.object({
      selectedUnitId: z.string().min(1, tStepper("selectRoomRequired")),
      selectedUnitTitle: z.string().optional(),
      fieldsToApply: z.array(z.string()).optional()
    });
    $[0] = tStepper;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const schema = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      selectedUnitId: "",
      selectedUnitTitle: "",
      fieldsToApply: []
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== schema) {
    t3 = zodResolver(schema);
    $[3] = schema;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = {
      defaultValues: t2,
      resolver: t3,
      confirmCloseOnDirty: false
    };
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const form = useAppForm(t4);
  let t5;
  if ($[7] !== form.control) {
    t5 = {
      control: form.control,
      name: "selectedUnitId"
    };
    $[7] = form.control;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const selectedUnitId = useWatch(t5);
  let t6;
  if ($[9] !== resource || $[10] !== selectedUnitId) {
    t6 = selectedUnitId ? findUnitAndPortfolio(resource, selectedUnitId) : undefined;
    $[9] = resource;
    $[10] = selectedUnitId;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const selection = t6;
  let t7;
  if ($[12] !== selection?.portfolio.imageUrls) {
    t7 = selection?.portfolio.imageUrls ?? [];
    $[12] = selection?.portfolio.imageUrls;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const propertyPhotos = t7;
  const {
    data: propertyDetail
  } = useSelectedPropertyDetail(selection?.portfolio.id ?? "", isBackofficeView);
  const detail = propertyDetail?.data;
  let t8;
  if ($[14] !== detail?.bathrooms || $[15] !== detail?.bedrooms || $[16] !== detail?.contract || $[17] !== detail?.houseFeatures || $[18] !== detail?.latitude || $[19] !== detail?.locationNormalizedName || $[20] !== detail?.longitude || $[21] !== detail?.petFriendly || $[22] !== detail?.smokingAllowed || $[23] !== selection) {
    t8 = selection ? {
      bedrooms: detail?.bedrooms ?? selection.portfolio.bedrooms,
      bathrooms: detail?.bathrooms ?? selection.portfolio.bathrooms,
      houseFeatures: detail?.houseFeatures ?? selection.portfolio.houseFeatures,
      latitude: detail?.latitude ?? selection.portfolio.latitude,
      longitude: detail?.longitude ?? selection.portfolio.longitude,
      locationNormalizedName: detail?.locationNormalizedName ?? selection.portfolio.locationNormalizedName,
      petFriendly: detail?.petFriendly ?? selection.portfolio.petFriendly,
      contract: detail?.contract ?? selection.portfolio.contract,
      smokingAllowed: detail?.smokingAllowed ?? selection.portfolio.smokingAllowed
    } : undefined;
    $[14] = detail?.bathrooms;
    $[15] = detail?.bedrooms;
    $[16] = detail?.contract;
    $[17] = detail?.houseFeatures;
    $[18] = detail?.latitude;
    $[19] = detail?.locationNormalizedName;
    $[20] = detail?.longitude;
    $[21] = detail?.petFriendly;
    $[22] = detail?.smokingAllowed;
    $[23] = selection;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const propertyShared = t8;
  const occupiedBy = selection?.unit.id !== currentUnitId ? selection?.unit.realEstateAssociated?.title : undefined;
  let t9;
  if ($[25] !== selection) {
    t9 = selection ? <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-0.5 text-xs text-primary-foreground"><span className="font-medium capitalize truncate max-w-[120px]">{selection.portfolio.title}</span>{selection.unit.title && <span className="opacity-80 capitalize truncate max-w-[140px]">· {selection.unit.title}</span>}</span> : undefined;
    $[25] = selection;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const associationBadge = t9;
  let t10;
  if ($[27] !== occupiedBy || $[28] !== tStepper) {
    t10 = occupiedBy ? <NotificationMessage variant="warning" hasCloseButton={false} isHtml={true} description={tStepper.markup("replaceWarning", {
      title: occupiedBy,
      b: _temp
    })} /> : undefined;
    $[27] = occupiedBy;
    $[28] = tStepper;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const reassignBanner = t10;
  let t11;
  if ($[30] !== tStepper) {
    t11 = tStepper("unitTitle");
    $[30] = tStepper;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== tStepper) {
    t12 = tStepper("unitDescription");
    $[32] = tStepper;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t) {
    t13 = t("currentBadge");
    $[34] = t;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== currentUnitId || $[37] !== resource || $[38] !== t13) {
    t14 = <AssociateUnitStep resource={resource} currentUnitId={currentUnitId} currentLabel={t13} />;
    $[36] = currentUnitId;
    $[37] = resource;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = ["selectedUnitId"];
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== reassignBanner || $[42] !== t11 || $[43] !== t12 || $[44] !== t14) {
    t16 = {
      id: 1,
      title: t11,
      description: t12,
      content: t14,
      warningBanner: reassignBanner,
      fieldNames: t15
    };
    $[41] = reassignBanner;
    $[42] = t11;
    $[43] = t12;
    $[44] = t14;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== tStepper) {
    t17 = tStepper("photosTitle");
    $[46] = tStepper;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== tStepper) {
    t18 = tStepper("photosDescription");
    $[48] = tStepper;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== listingId || $[51] !== propertyPhotos) {
    t19 = <AssociatePhotosStep propertyPhotos={propertyPhotos} listingId={listingId} />;
    $[50] = listingId;
    $[51] = propertyPhotos;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== listingId || $[54] !== propertyShared) {
    t20 = <AssociateFieldsStep property={propertyShared} listingId={listingId} />;
    $[53] = listingId;
    $[54] = propertyShared;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== t19 || $[57] !== t20) {
    t21 = <div className="flex flex-col gap-6">{t19}{t20}</div>;
    $[56] = t19;
    $[57] = t20;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = [];
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== t17 || $[61] !== t18 || $[62] !== t21) {
    t23 = {
      id: 2,
      title: t17,
      description: t18,
      content: t21,
      fieldNames: t22
    };
    $[60] = t17;
    $[61] = t18;
    $[62] = t21;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] !== t16 || $[65] !== t23) {
    t24 = [t16, t23];
    $[64] = t16;
    $[65] = t23;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  let t25;
  if ($[67] !== associationBadge || $[68] !== t24) {
    t25 = t24.map((step, index) => ({
      ...step,
      id: index + 1,
      titleBadge: associationBadge
    }));
    $[67] = associationBadge;
    $[68] = t24;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  const steps = t25;
  let t26;
  if ($[70] !== adminAssociateMutation || $[71] !== associateMutation || $[72] !== closeModal || $[73] !== isBackofficeView || $[74] !== listingId || $[75] !== onSuccess || $[76] !== propertyShared || $[77] !== updateListingMutation) {
    t26 = data => {
      if (!listingId) {
        return;
      }
      const finish = () => {
        onSuccess?.();
        closeModal();
      };
      const associate = isBackofficeView ? adminAssociateMutation : associateMutation;
      const fields = data.fieldsToApply ?? [];
      const applyThenFinish = () => {
        if (propertyShared && fields.length > 0) {
          updateListingMutation.mutate({
            listingId,
            data: buildSharedFieldsUpdate(propertyShared, fields)
          }, {
            onSuccess: finish,
            onError: finish
          });
        } else {
          finish();
        }
      };
      associate.mutate({
        listingId,
        resourceId: data.selectedUnitId,
        unitName: data.selectedUnitTitle
      }, {
        onSuccess: applyThenFinish
      });
    };
    $[70] = adminAssociateMutation;
    $[71] = associateMutation;
    $[72] = closeModal;
    $[73] = isBackofficeView;
    $[74] = listingId;
    $[75] = onSuccess;
    $[76] = propertyShared;
    $[77] = updateListingMutation;
    $[78] = t26;
  } else {
    t26 = $[78];
  }
  const handleOnSubmit = t26;
  const t27 = form as unknown as AppFormReturn<Record<string, any>>;
  let t28;
  if ($[79] !== tStepper) {
    t28 = tStepper("submit");
    $[79] = tStepper;
    $[80] = t28;
  } else {
    t28 = $[80];
  }
  const t29 = associateMutation.isPending || adminAssociateMutation.isPending;
  const t30 = !selectedUnitId;
  let t31;
  if ($[81] !== handleOnSubmit || $[82] !== steps || $[83] !== t27 || $[84] !== t28 || $[85] !== t29 || $[86] !== t30) {
    t31 = {
      form: t27,
      steps,
      handleOnSubmit,
      submitButtonText: t28,
      isSubmitting: t29,
      primaryActionDisabled: t30
    };
    $[81] = handleOnSubmit;
    $[82] = steps;
    $[83] = t27;
    $[84] = t28;
    $[85] = t29;
    $[86] = t30;
    $[87] = t31;
  } else {
    t31 = $[87];
  }
  return t31;
}
function _temp(chunks) {
  return `<b class="font-semibold">${chunks}</b>`;
}